import { DatePipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  InfiniteScrollCustomEvent,
  IonBackButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonList,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { withProductReviewsList } from '@libs/mtjr/shared/generated/entities/product-review/product-review-store-features';
import { signalStore } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';

const ReviewsStore = signalStore(withProductReviewsList());

@Component({
  selector: 'mtjr-product-reviews',
  providers: [ReviewsStore],
  imports: [
    IonContent,
    IonList,
    VxLoadingComponent,
    VxErrorComponent,
    IonText,
    IonCard,
    IonIcon,
    IonToolbar,
    IonHeader,
    IonTitle,
    IonButtons,
    IonBackButton,
    DatePipe,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    TranslatePipe,
  ],
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-title>{{ 'PRODUCTS.REVIEWS' | translate }}</ion-title>
        <ion-buttons slot="start">
          <ion-back-button [defaultHref]="'/products/' + productId" />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      @switch (store.productReviewsListState().type) {
        @case ('loading') {
          <mtjr-loading />
        }
        @case ('error') {
          <mtjr-error [failure]="store.productReviewsFailure()" />
        }
        @case ('success') {
          <div class="flex justify-center">
            <ion-list class="w-full max-w-[550px] bg-transparent pt-2">
              @for (review of store.productReviews(); track $index) {
                <ion-card class="!m-2 min-w-[250px] px-4 py-3">
                  <div class="flex h-full flex-col">
                    <div class="flex items-center">
                      <ion-text class="text-primary flex-1">{{ review.userName }}</ion-text>
                      <div class="flex" slot="end">
                        @for (i of [1, 2, 3, 4, 5]; track $index) {
                          @if (review.rating >= i) {
                            <ion-icon slot="icon-only" class="me-1 text-sm" color="warning" name="star-filled" />
                          } @else {
                            <ion-icon slot="icon-only" class="me-1 text-sm" color="warning" name="star" />
                          }
                        }
                      </div>
                    </div>
                    <ion-text color="medium" class="mt-2 flex-1">{{ review.review }}</ion-text>
                    <div class="bg-light mb-1 mt-2 h-[1px] w-full"></div>
                    <div class="flex">
                      <span class="text-medium ms-1 text-xs">{{ review.createdAt | date: 'dd-MM-yyyy' }}</span>
                    </div>
                  </div>
                </ion-card>
              }
            </ion-list>
          </div>
        }
      }
      <ion-infinite-scroll (ionInfinite)="onIonInfinite($event)">
        <ion-infinite-scroll-content></ion-infinite-scroll-content>
      </ion-infinite-scroll>
    </ion-content>
  `,
})
export class ProductReviewsPage {
  store = inject(ReviewsStore);
  route = inject(ActivatedRoute);
  productId: string = this.route.snapshot.params['productId'];

  constructor() {
    if (!this.productId) {
      throw new Error('productId is required');
    }
    this.store.productReviewsListPage({ productId: this.productId });
  }

  async onIonInfinite(event: InfiniteScrollCustomEvent) {
    await this.store.productReviewsListFetchNextPage({ productId: this.productId });
    await event?.target?.complete();
  }
}
